import type { RouteObject } from 'react-router';
import Spinner from '~/components/Spinner';
import env from '~/env';
import { RouteIds } from '~/utils/constants';
import { redirect } from '~/utils/routing';
import ErrorPage from './error';
import EditAddressPage from './features/address/edit/EditAddressPage';
import {
  action as editAddressAction,
  loader as editAddressLoader,
} from './features/address/edit/editAddress';
import AddressReviewPage from './features/address/review/AddressReviewPage';
import { loader as addressReviewLoader } from './features/address/review/addressReview';
import AppShellLayout from './features/app-shell/AppShellLayout';
import {
  loader as appShellLoader,
  shouldRevalidate as shouldRevalidateAppShell,
} from './features/app-shell/appShell';
import { claimLineItemMiddleware } from './features/crew/claim-line-items/claimLineItemMiddleware';
import DraftClaimLineItemLayout from './features/crew/claim-line-items/draft-layout/DraftClaimLineItemLayout';
import {
  loader as draftClaimLineItemLayoutLoader,
  shouldRevalidate as draftClaimLineItemLayoutShouldRevalidate,
} from './features/crew/claim-line-items/draft-layout/draftClaimLineItem';
import ClaimLineItemMetaPage from './features/crew/claim-line-items/meta/ClaimLineItemMetaPage';
import {
  action as claimLineItemMetaAction,
  loader as claimLineItemMetaLoader,
} from './features/crew/claim-line-items/meta/claimLineItemMeta';
import QuantityPage from './features/crew/claim-line-items/quantity/QuantityPage';
import {
  action as quantityAction,
  loader as quantityLoader,
} from './features/crew/claim-line-items/quantity/quantity';
import ReasonDetailPage from './features/crew/claim-line-items/reason-detail/ReasonDetailPage';
import {
  action as reasonDetailAction,
  loader as reasonDetailLoader,
} from './features/crew/claim-line-items/reason-detail/reasonDetail';
import ReasonFollowUpPage from './features/crew/claim-line-items/reason-follow-up/ReasonFollowUpPage';
import {
  action as reasonFollowUpAction,
  loader as reasonFollowUpLoader,
  shouldRevalidate as reasonFollowUpShouldRevalidate,
} from './features/crew/claim-line-items/reason-follow-up/reasonFollowUp';
import ClaimLineItemReasonPage from './features/crew/claim-line-items/reason/ClaimLineItemReasonPage';
import {
  action as claimLineItemReasonAction,
  loader as claimLineItemReasonLoader,
} from './features/crew/claim-line-items/reason/claimLineItemReason';
import ResolutionPage from './features/crew/claim-line-items/resolution/ResolutionPage';
import {
  action as resolutionAction,
  loader as resolutionLoader,
} from './features/crew/claim-line-items/resolution/resolution';
import ShippingProtectionPage from './features/crew/claim-line-items/shipping-protection/ShippingProtectionPage';
import { loader as shippingProtectionLoader } from './features/crew/claim-line-items/shipping-protection/shippingProtection';
import WarrantyWindowPage from './features/crew/claim-line-items/warranty-window/WarrantyWindowPage';
import { loader as warrantyWindowLoader } from './features/crew/claim-line-items/warranty-window/warrantyWindow';
import ClaimLineItemsSummaryPage from './features/crew/claim-line-items/summary/ClaimLineItemsSummaryPage';
import {
  action as claimLineItemsSummaryAction,
  loader as claimLineItemsSummaryLoader,
} from './features/crew/claim-line-items/summary/claimLineItemsSummary';
import ClaimOverviewPage from './features/crew/claim-overview/ClaimOverviewPage';
import { loader as claimOverviewLoader } from './features/crew/claim-overview/claimOverview';
import {
  crewClaimMiddleware,
  requireClaimLineItemsMiddleware,
  startClaimMiddleware,
} from './features/crew/crewClaimMiddleware';
import NewRequestPage from './features/crew/new-request/NewRequestPage';
import { loader as newRequestLoader } from './features/crew/new-request/newRequest';
import OrderEditPage from './features/crew/order-edit/OrderEditPage';
import OrderEditCommitPage from './features/crew/order-edit/commit/OrderEditCommitPage';
import {
  action as orderEditCommitAction,
  loader as orderEditCommitLoader,
} from './features/crew/order-edit/commit/orderEditCommit';
import {
  action as orderEditAction,
  loader as orderEditLoader,
} from './features/crew/order-edit/orderEdit';
import OrderEditStagePage from './features/crew/order-edit/stage/OrderEditStagePage';
import {
  action as orderEditStageAction,
  loader as orderEditStageLoader,
} from './features/crew/order-edit/stage/orderEditStage';
import OrderHubPage from './features/crew/order-hub/OrderHubPage';
import { loader as orderHubLoader } from './features/crew/order-hub/orderHub';
import ReturnMethodsErrorBoundary from './features/crew/return-methods/ReturnMethodsErrorBoundary';
import ReturnMethodsPage from './features/crew/return-methods/ReturnMethodsPage';
import {
  action as returnMethodsAction,
  loader as returnMethodsLoader,
} from './features/crew/return-methods/returnMethods';
import ReturnSummaryPage from './features/crew/return-summary/ReturnSummaryPage';
import {
  action as returnSummaryAction,
  loader as returnSummaryLoader,
} from './features/crew/return-summary/returnSummary';
import ShopPage from './features/crew/shop-now/ShopNowPage';
import ShopNowCompletePage from './features/crew/shop-now/complete/ShopNowCompletePage';
import { loader as shopCompleteLoader } from './features/crew/shop-now/complete/shopNowComplete';
import {
  action as shopAction,
  loader as shopLoader,
} from './features/crew/shop-now/shopNow';
import WarrantyReviewPage from './features/crew/warranty-review/WarrantyReviewPage';
import {
  action as warrantyReviewAction,
  loader as warrantyReviewLoader,
} from './features/crew/warranty-review/warrantyReview';
import OrderWithdrawPage from './features/crew/withdraw/OrderWithdraw';
import {
  action as orderWithdrawAction,
  loader as orderWithdrawLoader,
} from './features/crew/withdraw/withdraw';
import InactiveMerchantPage from './features/inactive-merchant/InactiveMerchantPage';
import {
  BasicOrderLookupPage,
  BrandedOrderLookupPage,
} from './features/order-lookup/OrderLookupPage';
import {
  crewOrderLookupAction,
  crewOrderLookupLoader,
  orderLookupAction,
} from './features/order-lookup/orderLookup';
import { middleware as orderMiddleware } from './features/order-middleware/orderMiddleware';
import ProtectionOrderNotProtectedPage from './features/protection-order/not-protected/ProtectionOrderNotProtectedPage';
import ProtectionOrderOverviewPage from './features/protection-order/overview/ProtectionOrderOverviewPage';
import {
  action as protectionOrderOverviewAction,
  loader as protectionOrderOverviewLoader,
} from './features/protection-order/overview/protectionOrderOverview';
import RegistrationPage from './features/registrations/RegistrationPage';
import CustomerInfoPage from './features/registrations/customer-info/CustomerInfoPage';
import {
  action as customerInfoAction,
  loader as customerInfoLoader,
} from './features/registrations/customer-info/customerInfo';
import {
  action as registrationAction,
  loader as registrationLoader,
} from './features/registrations/registration';
import RegistrationOverviewPage from './features/registrations/registration-overview/RegistrationOverviewPage';
import { loader as registrationOverviewLoader } from './features/registrations/registration-overview/registrationOverview';
import { registrationMiddleware } from './features/registrations/registrationMiddleware';
import SelectProductsPage from './features/registrations/select-products/SelectProductsPage';
import {
  action as selectProductsAction,
  loader as selectProductsLoader,
} from './features/registrations/select-products/selectProducts';
import ShippingProtectionClaimOverviewPage from './features/shipping-claim/claim-overview/ShippingClaimOverviewPage';
import { loader as shippingProtectionClaimOverviewLoader } from './features/shipping-claim/claim-overview/shippingClaimOverview';
import ShippingClaimItemsPage from './features/shipping-claim/new-claim/items/ShippingClaimItemsPage';
import {
  action as shippingClaimItemsAction,
  loader as shippingClaimItemsLoader,
} from './features/shipping-claim/new-claim/items/shippingClaimItems';
import ShippingClaimMetaPage from './features/shipping-claim/new-claim/meta/ShippingClaimMetaPage';
import {
  action as shippingClaimMetaAction,
  loader as shippingClaimMetaLoader,
} from './features/shipping-claim/new-claim/meta/shippingClaimMeta';
import ShippingClaimReasonPage from './features/shipping-claim/new-claim/reason/ShippingClaimReasonPage';
import {
  action as shippingClaimReasonAction,
  loader as shippingClaimReasonLoader,
} from './features/shipping-claim/new-claim/reason/shippingClaimReason';
import ShippingClaimReviewPage from './features/shipping-claim/new-claim/review/ShippingClaimReviewPage';
import {
  action as shippingClaimReviewAction,
  loader as shippingClaimReviewLoader,
} from './features/shipping-claim/new-claim/review/shippingClaimReview';
import ShippingIssuePage from './features/shipping-claim/new-claim/shipping-issue/ShippingIssuePage';
import { loader as shippingIssueLoader } from './features/shipping-claim/new-claim/shipping-issue/shippingIssue';
import { shippingClaimMiddleware } from './features/shipping-claim/new-claim/shippingClaimMiddleware';
import ColorsPage from './features/swatch/colors';
import TypographyPage from './features/swatch/typography';
import { middleware } from './settingsMiddleware';

export default {
  id: RouteIds.appShell,
  HydrateFallback: Spinner,
  middleware: [middleware],
  loader: appShellLoader,
  shouldRevalidate: shouldRevalidateAppShell,
  ErrorBoundary: ErrorPage,
  Component: AppShellLayout,
  children: [
    {
      id: 'orderLookup',
      index: true,
      action: orderLookupAction,
      Component: BasicOrderLookupPage,
    },
    // Crew routes
    {
      id: 'crew',
      path: '/:store',
      children: [
        // TODO: temp route for typography page
        ...(env.NOT_PROD
          ? [
              {
                path: 'typography',
                Component: TypographyPage,
              },
              {
                path: 'color',
                Component: ColorsPage,
              },
            ]
          : []),
        {
          id: 'crewOrderLookup',
          index: true,
          action: crewOrderLookupAction,
          loader: crewOrderLookupLoader,
          Component: BrandedOrderLookupPage,
        },
        {
          id: 'crewOrder',
          path: 'order/:idFromPlatform',
          middleware: [orderMiddleware],
          children: [
            {
              id: 'orderHub',
              index: true,
              Component: OrderHubPage,
              loader: orderHubLoader,
            },
            {
              id: 'orderWithdraw',
              path: 'withdraw',
              index: true,
              Component: OrderWithdrawPage,
              loader: orderWithdrawLoader,
              action: orderWithdrawAction,
            },
            {
              path: 'edit',
              children: [
                {
                  id: 'orderEdit',
                  index: true,
                  action: orderEditAction,
                  Component: OrderEditPage,
                  loader: orderEditLoader,
                },
                {
                  id: 'orderEditStage',
                  path: 'stage',
                  shouldRevalidate: () => false,
                  action: orderEditStageAction,
                  loader: orderEditStageLoader,
                  Component: OrderEditStagePage,
                },
                {
                  id: 'orderEditCommit',
                  path: 'commit',
                  shouldRevalidate: () => false,
                  action: orderEditCommitAction,
                  loader: orderEditCommitLoader,
                  Component: OrderEditCommitPage,
                },
              ],
            },
            {
              path: 'new-request',
              loader: newRequestLoader,
              Component: NewRequestPage,
            },
            {
              id: 'crewClaim',
              path: ':claimType',
              children: [
                {
                  id: 'claimLineItemsSummary',
                  index: true,
                  middleware: [startClaimMiddleware],
                  loader: claimLineItemsSummaryLoader,
                  Component: ClaimLineItemsSummaryPage,
                  action: claimLineItemsSummaryAction,
                },
                {
                  middleware: [crewClaimMiddleware],
                  children: [
                    {
                      middleware: [requireClaimLineItemsMiddleware],
                      children: [
                        {
                          id: 'shop',
                          path: 'shop',
                          children: [
                            {
                              index: true,
                              action: shopAction,
                              loader: shopLoader,
                              Component: ShopPage,
                            },
                            {
                              id: 'shopComplete',
                              path: ':shopNowId/complete',
                              loader: shopCompleteLoader,
                              Component: ShopNowCompletePage,
                            },
                          ],
                        },
                        {
                          id: 'address',
                          path: 'address',
                          children: [
                            {
                              id: 'addressReview',
                              index: true,
                              loader: addressReviewLoader,
                              Component: AddressReviewPage,
                            },
                            {
                              id: 'editAddress',
                              path: 'edit',
                              action: editAddressAction,
                              loader: editAddressLoader,
                              Component: EditAddressPage,
                            },
                          ],
                        },
                        {
                          id: 'returnMethods',
                          path: 'return-methods',
                          action: returnMethodsAction,
                          loader: returnMethodsLoader,
                          Component: ReturnMethodsPage,
                          ErrorBoundary: ReturnMethodsErrorBoundary,
                        },
                        {
                          id: 'reviewWarranty',
                          path: 'warranty-review',
                          Component: WarrantyReviewPage,
                          loader: warrantyReviewLoader,
                          action: warrantyReviewAction,
                        },
                        {
                          id: 'returnSummary',
                          path: 'return-summary',
                          Component: ReturnSummaryPage,
                          loader: returnSummaryLoader,
                          action: returnSummaryAction,
                          // //! don't want to run the loader again after the action call -- e.g. it will create a new payment intent if the payment confirmation fails on a declined card
                          // shouldRevalidate: () => false,
                        },
                      ],
                    },
                    {
                      id: 'draftClaimLineItemLayout',
                      path: ':draftId',
                      middleware: [claimLineItemMiddleware],
                      loader: draftClaimLineItemLayoutLoader,
                      Component: DraftClaimLineItemLayout,
                      shouldRevalidate:
                        draftClaimLineItemLayoutShouldRevalidate,
                      children: [
                        {
                          id: 'claimLineItemQuantity',
                          index: true,
                          Component: QuantityPage,
                          action: quantityAction,
                          loader: quantityLoader,
                        },
                        {
                          id: 'warrantyWindow',
                          path: 'warranty-window',
                          Component: WarrantyWindowPage,
                          loader: warrantyWindowLoader,
                        },
                        {
                          id: 'claimLineItemReason',
                          path: 'reason',
                          Component: ClaimLineItemReasonPage,
                          action: claimLineItemReasonAction,
                          loader: claimLineItemReasonLoader,
                        },
                        {
                          id: 'reasonDetail',
                          // ? tempted to simplify this to just reason-detail, not sure if really useful considering the id is on state
                          path: 'reason/:reasonId',
                          Component: ReasonDetailPage,
                          loader: reasonDetailLoader,
                          action: reasonDetailAction,
                        },
                        {
                          id: 'claimLineItemMeta',
                          path: 'meta',
                          Component: ClaimLineItemMetaPage,
                          loader: claimLineItemMetaLoader,
                          action: claimLineItemMetaAction,
                        },
                        {
                          id: 'reasonFollowUp',
                          path: 'follow-up',
                          Component: ReasonFollowUpPage,
                          loader: reasonFollowUpLoader,
                          action: reasonFollowUpAction,
                          shouldRevalidate: reasonFollowUpShouldRevalidate,
                        },
                        {
                          id: 'shippingProtection',
                          path: 'shipping-protection',
                          Component: ShippingProtectionPage,
                          loader: shippingProtectionLoader,
                        },
                        {
                          id: 'resolution',
                          path: 'resolution',
                          Component: ResolutionPage,
                          action: resolutionAction,
                          loader: resolutionLoader,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          id: 'inactiveMerchant',
          path: 'inactive',
          Component: InactiveMerchantPage,
        },
        {
          id: RouteIds.claimOverview,
          path: 'claim/overview/:claimExternalId',
          Component: ClaimOverviewPage,
          loader: claimOverviewLoader,
        },
        {
          id: 'register',
          path: 'register',
          middleware: [registrationMiddleware],
          children: [
            {
              index: true,
              id: 'registration',
              loader: registrationLoader,
              action: registrationAction,
              Component: RegistrationPage,
            },
            {
              id: 'customerInfo',
              path: 'customer-info',
              action: customerInfoAction,
              loader: customerInfoLoader,
              Component: CustomerInfoPage,
            },
            {
              id: 'selectProducts',
              path: 'product',
              action: selectProductsAction,
              loader: selectProductsLoader,
              Component: SelectProductsPage,
            },
            {
              id: 'registrationOverview',
              path: 'overview',
              Component: RegistrationOverviewPage,
              loader: registrationOverviewLoader,
            },
          ],
        },
      ],
    },
    // Shipping Protection routes
    {
      path: '/:store?/reorder/:idFromPlatform',
      middleware: [orderMiddleware],
      children: [
        {
          index: true,
          Component: ProtectionOrderOverviewPage,
          loader: protectionOrderOverviewLoader,
          action: protectionOrderOverviewAction,
        },
        {
          id: 'protectionOrderNotProtected',
          path: 'not-protected',
          Component: ProtectionOrderNotProtectedPage,
        },
        {
          id: 'shippingProtectionClaimOverview',
          path: 'claim/:claimId/:entryPoint?',
          Component: ShippingProtectionClaimOverviewPage,
          loader: shippingProtectionClaimOverviewLoader,
        },
        {
          path: 'new',
          middleware: [shippingClaimMiddleware],
          children: [
            {
              id: 'shippingClaimReason',
              index: true,
              Component: ShippingClaimReasonPage,
              loader: shippingClaimReasonLoader,
              action: shippingClaimReasonAction,
            },
            {
              id: 'shippingIssue',
              path: 'shipping-issue',
              Component: ShippingIssuePage,
              loader: shippingIssueLoader,
            },
            {
              id: 'shippingClaimItems',
              path: 'items',
              Component: ShippingClaimItemsPage,
              loader: shippingClaimItemsLoader,
              action: shippingClaimItemsAction,
            },
            {
              id: 'shippingClaimMeta',
              path: 'meta',
              Component: ShippingClaimMetaPage,
              loader: shippingClaimMetaLoader,
              action: shippingClaimMetaAction,
            },
            {
              id: 'shippingClaimAddressReview',
              path: 'address',
              Component: AddressReviewPage,
              loader: addressReviewLoader,
            },
            {
              id: 'shippingClaimAddressEdit',
              path: 'address/edit',
              Component: EditAddressPage,
              action: editAddressAction,
              loader: editAddressLoader,
            },
            {
              path: 'review',
              Component: ShippingClaimReviewPage,
              loader: shippingClaimReviewLoader,
              action: shippingClaimReviewAction,
            },
          ],
        },
      ],
    },
    {
      // Catch-all route to redirect to the store root
      path: '/:store?/*',
      loader: ({ params }) =>
        //! the crew order look up loader validates the store slug is not segment of a path
        redirect(`/${params.store ?? ''}`),
    },
  ],
} satisfies RouteObject;
